import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { TOOL_TIP_POSITIONS } from 'ui-components/services/tooltip-service';

import permissionStyles from '../../styles.module.less';
import styles from './styles.module.less';
import { PremiumAnalytics, ShipStationAnalytics } from '../../../../../../../constants/features';
import { emptyArray } from 'ui-components/default-props/empty';
import { UserPermissionService as Roles } from '../../../../../../../services/user-permission-service';
import Global from '../../../../../../../store/global-selectors';
import messages from '../../messages';

const ANALYTICS_RADIO_GROUP_NAME = 'analyticsRoleGroup';

type SellerUser = {
  userId: string;
  username?: string;
  roles?: string[];
};

type AdvancedAnalyticsPermissionProps = {
  permissionStatuses: Record<string, boolean>;
  updateForm: (field: string, value: boolean) => void;
  disabled: boolean;
  currentUser: SellerUser;
};

const getUserRoles = (user: SellerUser) => user?.roles ?? (emptyArray as string[]);

export const AdvancedAnalyticsPermission = ({
  permissionStatuses,
  updateForm,
  disabled,
  currentUser,
}: AdvancedAnalyticsPermissionProps) => {
  const intl = useIntl();
  const sellerUsers: SellerUser[] = useSelector(Global.selectSellerUsers);
  const sellerFeatures = useSelector(Global.selectSellerSellerFeatures);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  const hasAnalyticsFeature = sellerFeatures.includes(ShipStationAnalytics) || sellerFeatures.includes(PremiumAnalytics);
  const hasExploreFeature = sellerFeatures.includes(PremiumAnalytics);
  const licenseLimit = subscriptionData?.currentBillingPlan?.analyticsLicenseLimit?.limit ?? 0;

  const isChecked = permissionStatuses[Roles.analyticsViewRole] || permissionStatuses[Roles.analyticsExploreRole];
  const isExploreSelected = permissionStatuses[Roles.analyticsExploreRole];

  const currentUserHasSavedAnalyticsRole = useMemo(() => Roles.userHasAnyAnalyticsRole(getUserRoles(currentUser)), [currentUser]);

  const { analyticsUserNames, analyticsUserCount } = useMemo(() => {
    const analyticsUsers = sellerUsers
      .filter((user) => Roles.userHasAnyAnalyticsRole(getUserRoles(user)))
      .map((user) => user.username)
      .filter(Boolean);

    return {
      analyticsUserCount: analyticsUsers.length,
      analyticsUserNames: analyticsUsers.join(', '),
    };
  }, [sellerUsers]);

  const otherAnalyticsUserCount = useMemo(
    () => (currentUserHasSavedAnalyticsRole ? analyticsUserCount - 1 : analyticsUserCount),
    [analyticsUserCount, currentUserHasSavedAnalyticsRole],
  );

  const isLastAnalyticsUser = currentUserHasSavedAnalyticsRole && analyticsUserCount === 1;
  const areAllSeatsUsed = otherAnalyticsUserCount >= licenseLimit && !currentUserHasSavedAnalyticsRole;

  const getCheckboxTooltipMessage = () => {
    if (disabled) {
      return messages.cannotEditSelf;
    }
    if (!hasAnalyticsFeature) {
      return messages.analyticsUpgradeRequired;
    }
    if (isLastAnalyticsUser) {
      return messages.analyticsLastUserTooltip;
    }
    if (areAllSeatsUsed) {
      return hasExploreFeature ? messages.analyticsAllLicensesUsedPremium : messages.analyticsAllLicensesUsedStandard;
    }
    return null;
  };

  const isCheckboxDisabled = disabled || !hasAnalyticsFeature || isLastAnalyticsUser || areAllSeatsUsed;
  const tooltipMessage = getCheckboxTooltipMessage();

  const seatsUsed = analyticsUserCount;

  const handleCheckboxChange = useCallback(
    (value: boolean) => {
      if (value) {
        updateForm(Roles.analyticsViewRole, true);
        updateForm(Roles.analyticsExploreRole, false);
      } else {
        updateForm(Roles.analyticsViewRole, false);
        updateForm(Roles.analyticsExploreRole, false);
      }
    },
    [updateForm],
  );

  const handleExploreSelected = useCallback(() => {
    // we're using a "tiered" approach here, if a user is granted explore then that builds on top of view,
    // so they will have both features enabled but only consume a single license
    updateForm(Roles.analyticsExploreRole, true);
    updateForm(Roles.analyticsViewRole, true);
  }, [updateForm]);

  const handleViewOnlySelected = useCallback(() => {
    updateForm(Roles.analyticsViewRole, true);
    updateForm(Roles.analyticsExploreRole, false);
  }, [updateForm]);

  const checkboxLabel = intl.formatMessage(messages.advancedAnalytics);

  const CheckboxContent = (
    <div className={permissionStyles.perm}>
      <div className={styles.header}>
        <Checkbox
          checked={!!isChecked}
          className={styles.checkbox}
          disabled={isCheckboxDisabled}
          inputId='advancedAnalytics'
          label={checkboxLabel}
          onChange={handleCheckboxChange}
        />
        {seatsUsed > 0 ? (
          <Tooltip>
            <span className={styles['seat-counter']}>
              <FormattedMessage {...messages.analyticsSeatsAssigned} values={{ used: seatsUsed, total: licenseLimit }} />
            </span>
            <TooltipContent>
              <div>
                <FormattedMessage {...messages.analyticsLicensesInUseBy} values={{ userNames: analyticsUserNames }} />
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className={styles['seat-counter']}>
            <FormattedMessage {...messages.analyticsSeatsAssigned} values={{ used: seatsUsed, total: licenseLimit }} />
          </span>
        )}
      </div>
      <FormattedMessage {...messages.advancedAnalyticsDetails} />
      {isChecked && (
        <div className={styles['radio-group']}>
          <RadioCheckList>
            <div className={styles['radio-row']}>
              {!hasExploreFeature ? (
                <Tooltip>
                  <RadioCheckListItem
                    isChecked={false}
                    isDisabled
                    name={ANALYTICS_RADIO_GROUP_NAME}
                    onChecked={handleExploreSelected}
                    value={Roles.analyticsExploreRole}
                  >
                    <FormattedMessage {...messages.analyticsExploreAccess} />
                  </RadioCheckListItem>
                  <TooltipContent>
                    <div>
                      <FormattedMessage {...messages.analyticsExploreUpgradeRequired} />
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <RadioCheckListItem
                  className={styles['radio-button']}
                  isChecked={!!isExploreSelected}
                  isDisabled={disabled}
                  name={ANALYTICS_RADIO_GROUP_NAME}
                  onChecked={handleExploreSelected}
                  value={Roles.analyticsExploreRole}
                >
                  <FormattedMessage {...messages.analyticsExploreAccess} />
                </RadioCheckListItem>
              )}
              <RadioCheckListItem
                className={styles['radio-button']}
                isChecked={!isExploreSelected}
                isDisabled={disabled}
                name={ANALYTICS_RADIO_GROUP_NAME}
                onChecked={handleViewOnlySelected}
                value={Roles.analyticsViewRole}
              >
                <FormattedMessage {...messages.analyticsViewOnly} />
              </RadioCheckListItem>
            </div>
          </RadioCheckList>
        </div>
      )}
    </div>
  );

  if (!isCheckboxDisabled || !tooltipMessage) {
    return CheckboxContent;
  }

  return (
    <Tooltip position={TOOL_TIP_POSITIONS.topLeft}>
      {CheckboxContent}
      <TooltipContent>
        <div>
          <FormattedMessage {...tooltipMessage} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
